import { ContactForm } from '@/components/contacts/ContactForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  useAssetContacts,
  useCreateAssetContact,
  useDeleteContact,
} from '@/hooks/useContacts';
import type { CreateAssetContactDto } from '@/services/ContactService';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface AssetContactsProps {
  assetId: number;
}

export function AssetContacts({ assetId }: AssetContactsProps) {
  const [isAddingContact, setIsAddingContact] = useState(false);

  const { data: contacts, isLoading: isLoadingContacts } =
    useAssetContacts(assetId);
  const { mutate: createContact, isPending: isCreating } =
    useCreateAssetContact(assetId);
  const { mutate: deleteContact, isPending: isDeleting } = useDeleteContact();

  const handleAddContact = async (data: CreateAssetContactDto) => {
    createContact(data, {
      onSuccess: () => {
        setIsAddingContact(false);
      },
    });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      deleteContact(id);
    }
  };

  if (isLoadingContacts) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Contacts</h2>
        <Sheet open={isAddingContact} onOpenChange={setIsAddingContact}>
          <SheetTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Add New Contact</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <ContactForm
                onSubmit={handleAddContact}
                isLoading={isCreating}
                isAssetContact
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {contacts?.map((contact) => (
          <Card key={contact.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xl font-medium">
                {contact.first_name} {contact.last_name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(contact.id)}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  <strong>Email:</strong> {contact.email}
                </p>
                {contact.phone && (
                  <p className="text-sm text-muted-foreground">
                    <strong>Phone:</strong> {contact.phone}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  <strong>Role:</strong> {contact.role}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}

        {contacts?.length === 0 && (
          <div className="col-span-2 rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground">No contacts added yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
